import clsx from 'clsx'
import { format } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { match } from 'ts-pattern'
import { z } from 'zod'

import Header from '@/components-latest/layout/Header'
import Banner from '@/components-latest/primitives/Banner'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import {
  ExclamationPoint,
  InfoCircle
} from '@/components-latest/primitives/Icons'
import Store from '@/components-latest/primitives/Icons/Store'
import Spinner from '@/components-latest/primitives/Spinner'
import SwitchWithWords from '@/components-latest/primitives/SwitchWithWords'
import Text, { textVariants } from '@/components-latest/primitives/Text'
import BusinessHoursForm from '@/components-latest/ui/AccountPage/BusinessHoursForm'
import BusinessHoursForm5Slots from '@/components-latest/ui/AccountPage/BusinessHoursForm5Slots'
import EditStoreInfoForm from '@/components-latest/ui/AccountPage/EditStoreInfoForm'
import OnlineRestriction from '@/components-latest/ui/OnlineRestriction/OnlineRestriction'
import { ApiGetBranchBusinessHoursSchema } from '@/ducks/account/account.schema'
import {
  useLazyFetchBranchBusinessHoursQuery,
  useLazyFetchStoreInfoQuery
} from '@/ducks/account/api/account.api'
import { useAccessControl } from '@/ducks/auth/auth.permissions'
import {
  useChangeStoreAvailabilityMutation,
  useFetchAllStoreBranchesQuery,
  useSetBranchAvailabilityMutation
} from '@/ducks/store/api/store.api'
import {
  selectSelectedBranchId,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import { setSelectedBranchId } from '@/ducks/store/slice/store.slice'
import { useToggle } from '@/hooks/useToggle/useToggle'
import { logFirebaseEvent } from '@/utils/firebase.utils'

import Styles from './BranchManagementPage.module.scss'

const AccountPage = () => {
  const { t } = useTranslation()
  const { store_web_app_additional_time_slots } = useFlags()
  const { canEditBusinessHours } = useAccessControl()
  const history = useHistory()
  const dispatch = useDispatch()
  type ApiGetBranchBusinessHours = z.infer<
    typeof ApiGetBranchBusinessHoursSchema
  >
  const { data: branches } = useFetchAllStoreBranchesQuery()
  const [isEditFormVisible, toggleEditFormVisibility] = useToggle(false)
  const selectedBranchId = useSelector(selectSelectedBranchId)
  const storeId = useSelector(selectStoreId) || ''
  const storeName = ''
  const [fetchStoreInfo, { data, isFetching }] = useLazyFetchStoreInfoQuery()
  const [fetchBranchBusinessHours, fetchBranchBusinessHoursQuery] =
    useLazyFetchBranchBusinessHoursQuery()
  const [checked, setChecked] = useState(true)
  const [selectedBranchName, setSelectedBranchName] = useState<string>('')
  const [selectedBusinessHours, setSelectedBusinessHours] =
    useState<ApiGetBranchBusinessHours | null>(null)

  const [isOnlineRestrictionOpen, setIsOnlineRestrictionOpen] = useState(false)
  const [setBranchAvailability, { isLoading: isChangingAvailability }] =
    useSetBranchAvailabilityMutation()

  const branchAvailability = branches?.find(
    b => b.store_id === selectedBranchId
  )?.availability

  const branchPostRejection = branches?.find(
    b => b.store_id === selectedBranchId
  )?.isPostRejection

  useEffect(() => {
    if (storeId) {
      fetchStoreInfo({ storeId })
      fetchBranchBusinessHours({ storeId })
    }
  }, [storeId, fetchStoreInfo, fetchBranchBusinessHours])

  useEffect(() => {
    if (
      fetchBranchBusinessHoursQuery.isSuccess &&
      Array.isArray(fetchBranchBusinessHoursQuery.data)
    ) {
      const matched = fetchBranchBusinessHoursQuery.data.find(
        entry => entry.store_id === selectedBranchId
      )
      if (matched) {
        setSelectedBusinessHours(matched)
      }
    }
  }, [fetchBranchBusinessHoursQuery.data, selectedBranchId])

  useEffect(() => {
    if (!selectedBranchName) {
      const branchName = branches?.[0]?.address
      dispatch(setSelectedBranchId(branches?.[0]?.store_id || ''))
      setSelectedBranchName(branchName || '')
    } else {
      const matchedBranch = branches?.find(
        restaurant => restaurant.store_id === selectedBranchId
      )
      const branchName = matchedBranch?.address
      setSelectedBranchName(branchName || '')
    }
  }, [branches, selectedBranchId, storeName])

  const handleBranchChange = (newBranchId: string) => {
    dispatch(setSelectedBranchId(newBranchId))
  }
  const [changeStoreAvailability, { isLoading }] =
    useChangeStoreAvailabilityMutation()

  const onSwitchToggle = () => {
    if (!selectedBranchId || branchAvailability === undefined) return

    const newAvailability = branchAvailability === 1 ? 0 : 1

    if (newAvailability === 1) {
      if (branchPostRejection) {
        setIsOnlineRestrictionOpen(true)
        return
      }
      setBranchAvailability({
        store_id: selectedBranchId,
        availability: newAvailability
      })
        .unwrap()
        .then(() => {
          logFirebaseEvent('AC_Web_Toggle_On', {
            Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
            Store_ID: storeId
          })
        })
        .catch(error => {
          if (error?.errorCode === 'OPENING_RESTRICTED') {
            setIsOnlineRestrictionOpen(false)
            setTimeout(() => setIsOnlineRestrictionOpen(true), 0)
          }
        })
    } else {
      setBranchAvailability({
        store_id: selectedBranchId,
        availability: newAvailability
      })
        .unwrap()
        .then(() => {
          logFirebaseEvent('AC_Web_Branch_Toggle_Off', {
            Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
            Branch_ID: selectedBranchId
          })
        })
    }
  }

  return (
    <div className={Styles.page}>
      <Header
        onGoBack={isEditFormVisible ? toggleEditFormVisibility : undefined}
        title="Branches">
        {Array.isArray(branches) && branches.length > 0 && (
          <Select onValueChange={handleBranchChange} value={selectedBranchId}>
            <SelectTrigger
              arrowVariant="solidArrow"
              className={clsx(
                Styles.selectTrigger,
                textVariants({ variant: 'nextBody' })
              )}>
              <Store />
              <SelectValue asChild>
                <Text variant="nextBody">
                  {
                    branches.find(
                      branch => branch.store_id === selectedBranchId
                    )?.area
                  }
                </Text>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className={Styles.selectContent}>
              {branches.map(branch => (
                <SelectItem key={branch.store_id} value={branch.store_id}>
                  {branch.area}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Header>
      <Banner
        description="Manage your branches’ business hours and availability here."
        icon={<InfoCircle />}
        type="plain"
        state="normal"
      />
      {!isFetching && data && data.inReview && (
        <div className={Styles.inReview}>
          <ExclamationPoint />
          <Text variant="body" weight="semiBold">
            {t('account.in-review')}
          </Text>
        </div>
      )}
      <div className={Styles.content}>
        {match({ isFetching, data, isEditFormVisible })
          .when(
            ({ data, isFetching }) => !data || isFetching,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .with({ isEditFormVisible: true }, () => (
            <EditStoreInfoForm
              initialStoreInfo={data}
              closeEditForm={toggleEditFormVisibility}
            />
          ))
          .with({ isEditFormVisible: false }, () => (
            <>
              <ContentBlock>
                {match(fetchBranchBusinessHoursQuery as any)
                  .with({ isLoading: true }, () => (
                    <ContentBlock className={Styles.hoursSpinner}>
                      <Spinner />
                    </ContentBlock>
                  ))
                  .with({ isSuccess: true }, ({ data }: any) => {
                    if (!Array.isArray(data) || data.length === 0) {
                      history.push('/account')
                      return null
                    }
                    return (
                      <div>
                        <div className={clsx(Styles.accordion)}>
                          <div className={Styles.header}>
                            <div>
                              <Text
                                variant="nextSubtitle"
                                weight="bold"
                                className={Styles.title}>
                                <span className={Styles.iconWrapper}>
                                  <Store />
                                </span>
                                {selectedBranchName}
                              </Text>
                            </div>
                            <div>
                              <SwitchWithWords
                                onToggle={onSwitchToggle}
                                checked={branchAvailability === 1}
                                disabled={!canEditBusinessHours}
                                isLoading={isChangingAvailability}
                                className={Styles.switch}
                                uncheckedWord={t('Close')}
                                checkedWord={t('Open')}
                                width={100}
                              />

                              {isOnlineRestrictionOpen && (
                                <OnlineRestriction
                                  onClose={() =>
                                    setIsOnlineRestrictionOpen(false)
                                  }>
                                  <div />
                                </OnlineRestriction>
                              )}
                            </div>
                          </div>
                          <div className={Styles.contentMain}>
                            {store_web_app_additional_time_slots ? (
                              <BusinessHoursForm5Slots
                                key={
                                  selectedBusinessHours?.store_id ||
                                  data?.[0].store_id
                                }
                                initialData={selectedBusinessHours ?? data?.[0]}
                                canEdit={canEditBusinessHours}
                              />
                            ) : (
                              <BusinessHoursForm
                                key={
                                  selectedBusinessHours?.store_id ||
                                  data?.[0].store_id
                                }
                                initialData={selectedBusinessHours ?? data?.[0]}
                                canEdit={canEditBusinessHours}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                  .otherwise(() => null)}
              </ContentBlock>
            </>
          ))
          .otherwise(() => null)}
      </div>
    </div>
  )
}

export default AccountPage
